import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PageHeader, PageLoader, EmptyState } from '@/components/common/Layout';
import { RiskBadge } from '@/components/common/RiskBadge';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/lib/api';
import { canManageAssets, useAuth } from '@/lib/auth';
import { fmtCoord, fmtDate } from '@/lib/format';
import { toast } from 'sonner';
import { Plus, MapPin, Landmark } from 'lucide-react';

export default function AssetsPage() {
  const { user } = useAuth();
  const [assets, setAssets] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get('/assets').then(r => setAssets(r.data));
  useEffect(() => { load(); }, []);

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      <PageHeader
        title="Assets"
        subtitle="Critical infrastructure under continuous satellite observation."
        actions={canManageAssets(user.role) ? (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-asset-btn"><Plus size={14} className="mr-1" /> New asset</Button>
            </DialogTrigger>
            <CreateAssetDialog onCreated={() => { setOpen(false); load(); }} onCancel={() => setOpen(false)} />
          </Dialog>
        ) : null}
        testId="assets-header"
      />

      {!assets ? <PageLoader /> : assets.length === 0 ? (
        <EmptyState title="No assets yet" hint="Create your first asset to start monitoring." />
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="assets-grid">
          {assets.map((a) => (
            <Link key={a.id} to={`/app/assets/${a.id}`} data-testid={`asset-card-${a.id}`}>
              <Card className="p-4 border-border hover:border-primary/40 transition-colors h-full">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{a.category}</div>
                    <div className="font-semibold mt-1 flex items-center gap-2"><Landmark size={16} className="text-primary" /> {a.name}</div>
                  </div>
                  <RiskBadge severity={a.current_risk_level} />
                </div>
                <div className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground">
                  <MapPin size={12} /> {a.location_name}
                </div>
                <div className="mt-2 text-xs text-muted-foreground font-mono">
                  {fmtCoord(a.lat)}, {fmtCoord(a.lng)}
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {(a.enabled_modules || []).map(m => (
                    <Badge key={m} variant="outline" className="text-[10px]">{m}</Badge>
                  ))}
                </div>
                {a.hydropower?.installed_capacity_mw && (
                  <div className="mt-3 pt-3 border-t border-border text-xs grid grid-cols-2 gap-x-3 gap-y-1">
                    <div className="text-muted-foreground">Capacity</div><div className="font-mono">{a.hydropower.installed_capacity_mw} MW</div>
                    <div className="text-muted-foreground">Dam height</div><div className="font-mono">{a.hydropower.dam_height_m ?? '—'} m</div>
                    <div className="text-muted-foreground">Reservoir</div><div className="font-mono">{a.hydropower.reservoir_capacity_mcm ?? '—'} MCM</div>
                    <div className="text-muted-foreground">Commissioned</div><div className="font-mono">{a.commissioning_year ?? '—'}</div>
                  </div>
                )}
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function CreateAssetDialog({ onCreated, onCancel }) {
  const [f, setF] = useState({
    name: '', category: 'Hydropower', description: '',
    location_name: '', region: '', lat: '30.38', lng: '78.48',
    criticality: 'High', owner: '', commissioning_year: '',
    hydro: {
      dam_type: '', dam_height_m: '', reservoir_capacity_mcm: '',
      generating_units: '', installed_capacity_mw: '', river_basin: '',
      full_reservoir_level_m: '', catchment_area_km2: '',
    },
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const size = 0.03;
      const lat = parseFloat(f.lat), lng = parseFloat(f.lng);
      const body = {
        ...f,
        lat, lng,
        commissioning_year: f.commissioning_year ? parseInt(f.commissioning_year, 10) : null,
        boundary: {
          coords: [[lat - size, lng - size], [lat - size, lng + size], [lat + size, lng + size], [lat + size, lng - size], [lat - size, lng - size]],
        },
        hydropower: {
          dam_type: f.hydro.dam_type || null,
          dam_height_m: f.hydro.dam_height_m ? parseFloat(f.hydro.dam_height_m) : null,
          reservoir_capacity_mcm: f.hydro.reservoir_capacity_mcm ? parseFloat(f.hydro.reservoir_capacity_mcm) : null,
          generating_units: f.hydro.generating_units ? parseInt(f.hydro.generating_units, 10) : null,
          installed_capacity_mw: f.hydro.installed_capacity_mw ? parseFloat(f.hydro.installed_capacity_mw) : null,
          river_basin: f.hydro.river_basin || null,
          full_reservoir_level_m: f.hydro.full_reservoir_level_m ? parseFloat(f.hydro.full_reservoir_level_m) : null,
          catchment_area_km2: f.hydro.catchment_area_km2 ? parseFloat(f.hydro.catchment_area_km2) : null,
        },
      };
      delete body.hydro;
      await api.post('/assets', body);
      toast.success('Asset created');
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create asset');
    } finally { setBusy(false); }
  };
  return (
    <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
      <DialogHeader><DialogTitle>Create new asset</DialogTitle></DialogHeader>
      <div className="grid md:grid-cols-2 gap-3 mt-2">
        <div><Label>Name *</Label><Input value={f.name} onChange={e => setF({ ...f, name: e.target.value })} data-testid="asset-form-name" /></div>
        <div><Label>Category</Label><Input value={f.category} onChange={e => setF({ ...f, category: e.target.value })} /></div>
        <div className="md:col-span-2"><Label>Description</Label><Textarea rows={2} value={f.description} onChange={e => setF({ ...f, description: e.target.value })} /></div>
        <div><Label>Location name</Label><Input value={f.location_name} onChange={e => setF({ ...f, location_name: e.target.value })} data-testid="asset-form-location" /></div>
        <div><Label>Region</Label><Input value={f.region} onChange={e => setF({ ...f, region: e.target.value })} /></div>
        <div><Label>Latitude *</Label><Input type="number" step="0.0001" value={f.lat} onChange={e => setF({ ...f, lat: e.target.value })} data-testid="asset-form-lat" /></div>
        <div><Label>Longitude *</Label><Input type="number" step="0.0001" value={f.lng} onChange={e => setF({ ...f, lng: e.target.value })} data-testid="asset-form-lng" /></div>
        <div><Label>Commissioning year</Label><Input type="number" value={f.commissioning_year} onChange={e => setF({ ...f, commissioning_year: e.target.value })} /></div>
        <div><Label>Criticality</Label>
          <Select value={f.criticality} onValueChange={v => setF({ ...f, criticality: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Low">Low</SelectItem>
              <SelectItem value="Medium">Medium</SelectItem>
              <SelectItem value="High">High</SelectItem>
              <SelectItem value="Critical">Critical</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label>Owner</Label><Input value={f.owner} onChange={e => setF({ ...f, owner: e.target.value })} /></div>
        <div className="md:col-span-2 text-xs uppercase tracking-widest text-muted-foreground mt-3 border-t border-border pt-3">Hydropower details (optional)</div>
        <div><Label>Dam type</Label><Input value={f.hydro.dam_type} onChange={e => setF({ ...f, hydro: { ...f.hydro, dam_type: e.target.value } })} /></div>
        <div><Label>Dam height (m)</Label><Input type="number" value={f.hydro.dam_height_m} onChange={e => setF({ ...f, hydro: { ...f.hydro, dam_height_m: e.target.value } })} /></div>
        <div><Label>Reservoir capacity (MCM)</Label><Input type="number" value={f.hydro.reservoir_capacity_mcm} onChange={e => setF({ ...f, hydro: { ...f.hydro, reservoir_capacity_mcm: e.target.value } })} /></div>
        <div><Label>Installed capacity (MW)</Label><Input type="number" value={f.hydro.installed_capacity_mw} onChange={e => setF({ ...f, hydro: { ...f.hydro, installed_capacity_mw: e.target.value } })} /></div>
        <div><Label>Generating units</Label><Input type="number" value={f.hydro.generating_units} onChange={e => setF({ ...f, hydro: { ...f.hydro, generating_units: e.target.value } })} /></div>
        <div><Label>River basin</Label><Input value={f.hydro.river_basin} onChange={e => setF({ ...f, hydro: { ...f.hydro, river_basin: e.target.value } })} /></div>
      </div>
      <DialogFooter className="mt-4">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={submit} disabled={busy || !f.name} data-testid="asset-form-submit">{busy ? 'Creating…' : 'Create asset'}</Button>
      </DialogFooter>
    </DialogContent>
  );
}
